import { useState, useEffect, useCallback } from 'react';
import { useUser } from '../contexts/AuthContext';
import employeeService from '../services/api/employeeService';

export const useEmployees = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data state
  const [employees, setEmployees] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    terminated: 0
  });

  // Pagination and filtering state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,  // Changed from 50 to 10 for better UX
    totalItems: 0,
    totalPages: 0,
    totalCount: 0  // Add totalCount for consistency
  });

  const [filters, setFilters] = useState({
    status: '',
    teamId: '',
    search: '',
    limit: 10  // Track limit in filters too
  });

  // Fetch employees with current filters and pagination
  const fetchEmployees = useCallback(async (silent = false) => {
    if (!user?.id) return;
    
    try {
      // Only show loading spinner on initial load, not on background refreshes
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await employeeService.getEmployees(params);
      
      setEmployees(response.employees || []);
      setPagination(prev => ({
        ...prev,
        totalItems: response.pagination?.totalCount || 0,
        totalPages: response.pagination?.totalPages || 0,
        totalCount: response.pagination?.totalCount || 0  // Add for consistency
      }));
    } catch (err) {
      console.error('Failed to fetch employees:', err);
      setError('Failed to load employees. Please try again.');
      setEmployees([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [user?.id, pagination.page, pagination.limit, filters]);

  // Fetch statistics separately
  const fetchStatistics = useCallback(async () => {
    if (!user?.id) return;

    try {
      const stats = await employeeService.getStatistics();
      setStatistics(stats.statistics || {
        total: 0,
        active: 0,
        inactive: 0,
        terminated: 0
      });
    } catch (err) {
      console.error('Failed to fetch employee statistics:', err);
      // Don't set error for statistics failure, just use defaults
    }
  }, [user?.id]);

  // Initial data fetch
  useEffect(() => {
    fetchEmployees();
    fetchStatistics();
  }, [fetchEmployees, fetchStatistics]);

  // Update filters and reset to first page
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    // If limit changed, update pagination.limit too
    if (newFilters.limit) {
      setPagination(prev => ({ ...prev, page: 1, limit: newFilters.limit }));
    } else {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, []);

  // Change page
  const changePage = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  // Employee operations
  const employeeOperations = {
    // Create employee
    create: useCallback(async (employeeData) => {
      try {
        setError(null);
        const response = await employeeService.createEmployee(employeeData);

        // Refresh data after creation (silent to prevent loading spinner)
        await fetchEmployees(true);
        await fetchStatistics();

        return response;
      } catch (err) {
        console.error('Failed to create employee:', err);
        const errorMessage = err.response?.data?.error || 'Failed to create employee. Please try again.';
        setError(errorMessage);
        throw err;
      }
    }, [fetchEmployees, fetchStatistics]),

    // Update employee
    update: useCallback(async (employeeId, updateData) => {
      try {
        setError(null);
        await employeeService.updateEmployee(employeeId, updateData);
        
        // Refresh data after update (silent to prevent loading spinner)
        await fetchEmployees(true);
        await fetchStatistics();
        
        return true;
      } catch (err) {
        console.error('Failed to update employee:', err);
        const errorMessage = err.response?.data?.error || 'Failed to update employee. Please try again.';
        setError(errorMessage);
        throw err;
      }
    }, [fetchEmployees, fetchStatistics]),

    // Change employee status
    changeStatus: useCallback(async (employeeId, newStatus, reason = null) => {
      try {
        setError(null);
        await employeeService.changeStatus(employeeId, newStatus, reason);
        
        // Refresh data after status change (silent to prevent loading spinner)
        await fetchEmployees(true);
        await fetchStatistics();
        
        return true;
      } catch (err) {
        console.error('Failed to change employee status:', err);
        const errorMessage = err.response?.data?.error || 'Failed to change employee status. Please try again.';
        setError(errorMessage);
        throw err;
      }
    }, [fetchEmployees, fetchStatistics]),

    // Get single employee
    getById: useCallback(async (employeeId) => {
      try {
        setError(null);
        const response = await employeeService.getEmployee(employeeId);
        return response.employee;
      } catch (err) {
        console.error('Failed to fetch employee:', err);
        const errorMessage = err.response?.data?.error || 'Failed to load employee. Please try again.';
        setError(errorMessage);
        throw err;
      }
    }, [])
  };

  // Refresh all data (silent mode prevents loading spinner)
  const refreshData = useCallback(async (silent = true) => {
    await fetchEmployees(silent);
    await fetchStatistics();
  }, [fetchEmployees, fetchStatistics]);

  return {
    // Data
    employees,
    statistics,
    pagination,
    filters,
    
    // State
    loading,
    error,
    setError,
    
    // Actions
    employeeOperations,
    setFilters: handleFiltersChange,
    changePage,
    refreshData
  };
};